import React, { useState, useEffect } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import ProcessingStatusBadge from '../../components/ui/ProcessingStatusBadge';
import AmbiguousMatchesCounter from '../../components/ui/AmbiguousMatchesCounter';
import ExportQueueIndicator from '../../components/ui/ExportQueueIndicator';
import MetricsCard from './components/MetricsCard';
import FilterControls from './components/FilterControls';
import ProductsTable from './components/ProductsTable';
import SupplierAnalytics from './components/SupplierAnalytics';
import ExportDropdown from './components/ExportDropdown';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ResultsDashboard = () => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showAnalytics, setShowAnalytics] = useState(false);

  // Mock data for products
  const mockProducts = [
    {
      id: 'prod_001',
      productName: 'iPhone 15 Pro Max 256GB Natural Titanium',
      brand: 'Apple',
      mpn: 'MU793LL/A',
      ean: '194253433620',
      bestPrice: 1199.99,
      bestPriceSupplier: 'TechWorld Supply',
      matchType: 'exact_ean',
      confidence: 1.0,
      supplierCount: 4,
      allSuppliers: [
        {
          name: 'TechWorld Supply',
          price: 1199.99,
          stock: 25,
          lastUpdated: '2 hours ago',
          isBestPrice: true
        },
        {
          name: 'Global Electronics',
          price: 1249.99,
          stock: 18,
          lastUpdated: '4 hours ago',
          isBestPrice: false
        },
        {
          name: 'Digital Depot',
          price: 1299.99,
          stock: 12,
          lastUpdated: '1 day ago',
          isBestPrice: false
        },
        {
          name: 'Component Plus',
          price: 1279.99,
          stock: 0,
          lastUpdated: '3 days ago',
          isBestPrice: false
        }
      ]
    },
    {
      id: 'prod_002',
      productName: 'Samsung Galaxy S24 Ultra 512GB Titanium Black',
      brand: 'Samsung',
      mpn: 'SM-S928UZKAXAA',
      ean: '887276707471',
      bestPrice: 1299.99,
      bestPriceSupplier: 'Global Electronics',
      matchType: 'exact_mpn',
      confidence: 0.98,
      supplierCount: 3,
      allSuppliers: [
        {
          name: 'Global Electronics',
          price: 1299.99,
          stock: 15,
          lastUpdated: '1 hour ago',
          isBestPrice: true
        },
        {
          name: 'TechWorld Supply',
          price: 1349.99,
          stock: 22,
          lastUpdated: '3 hours ago',
          isBestPrice: false
        },
        {
          name: 'Circuit City Wholesale',
          price: 1399.99,
          stock: 8,
          lastUpdated: '6 hours ago',
          isBestPrice: false
        }
      ]
    },
    {
      id: 'prod_003',
      productName: 'Sony WH-1000XM5 Wireless Noise Canceling Headphones',
      brand: 'Sony',
      mpn: 'WH1000XM5/B',
      ean: '027242920156',
      bestPrice: 349.99,
      bestPriceSupplier: 'Component Plus',
      matchType: 'ai_fuzzy',
      confidence: 0.89,
      supplierCount: 5,
      allSuppliers: [
        {
          name: 'Component Plus',
          price: 349.99,
          stock: 45,
          lastUpdated: '30 minutes ago',
          isBestPrice: true
        },
        {
          name: 'Digital Depot',
          price: 369.99,
          stock: 32,
          lastUpdated: '2 hours ago',
          isBestPrice: false
        },
        {
          name: 'TechWorld Supply',
          price: 379.99,
          stock: 28,
          lastUpdated: '4 hours ago',
          isBestPrice: false
        },
        {
          name: 'Global Electronics',
          price: 389.99,
          stock: 19,
          lastUpdated: '1 day ago',
          isBestPrice: false
        },
        {
          name: 'Circuit City Wholesale',
          price: 399.99,
          stock: 12,
          lastUpdated: '2 days ago',
          isBestPrice: false
        }
      ]
    },
    {
      id: 'prod_004',
      productName: 'Dell XPS 13 Laptop Intel Core i7 16GB RAM 512GB SSD',
      brand: 'Dell',
      mpn: 'XPS9320-7302SLV-PUS',
      ean: '',
      bestPrice: 1099.99,
      bestPriceSupplier: 'Digital Depot',
      matchType: 'ambiguous',
      confidence: 0.85,
      supplierCount: 2,
      allSuppliers: [
        {
          name: 'Digital Depot',
          price: 1099.99,
          stock: 8,
          lastUpdated: '5 hours ago',
          isBestPrice: true
        },
        {
          name: 'TechWorld Supply',
          price: 1149.99,
          stock: 12,
          lastUpdated: '1 day ago',
          isBestPrice: false
        }
      ]
    },
    {
      id: 'prod_005',
      productName: 'LG OLED55C3PUA 55" C3 Series OLED 4K Smart TV',
      brand: 'LG',
      mpn: 'OLED55C3PUA',
      ean: '195174051962',
      bestPrice: 1399.99,
      bestPriceSupplier: 'Circuit City Wholesale',
      matchType: 'exact_ean',
      confidence: 1.0,
      supplierCount: 4,
      allSuppliers: [
        {
          name: 'Circuit City Wholesale',
          price: 1399.99,
          stock: 6,
          lastUpdated: '3 hours ago',
          isBestPrice: true
        },
        {
          name: 'Global Electronics',
          price: 1449.99,
          stock: 9,
          lastUpdated: '6 hours ago',
          isBestPrice: false
        },
        {
          name: 'Digital Depot',
          price: 1499.99,
          stock: 4,
          lastUpdated: '12 hours ago',
          isBestPrice: false
        },
        {
          name: 'TechWorld Supply',
          price: 1549.99,
          stock: 3,
          lastUpdated: '1 day ago',
          isBestPrice: false
        }
      ]
    }
  ];

  // Summary metrics
  const summaryMetrics = {
    totalProducts: 722,
    matchesFound: 696,
    potentialSavings: 45678.50,
    ambiguousMatches: 26,
    exactMatches: 540,
    aiMatches: 156,
    processingTime: '4m 32s'
  };

  useEffect(() => {
    setFilteredProducts(mockProducts);
  }, []);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    // Apply filters to products
    let filtered = mockProducts;
    
    if (newFilters?.search) {
      const searchTerm = newFilters?.search?.toLowerCase();
      filtered = filtered?.filter(product => 
        product?.productName?.toLowerCase()?.includes(searchTerm) ||
        product?.brand?.toLowerCase()?.includes(searchTerm) ||
        product?.mpn?.toLowerCase()?.includes(searchTerm) ||
        product?.ean?.includes(searchTerm)
      );
    }
    
    if (newFilters?.matchType) {
      filtered = filtered?.filter(product => product?.matchType === newFilters?.matchType);
    }
    
    if (newFilters?.brand) {
      filtered = filtered?.filter(product => product?.brand?.toLowerCase() === newFilters?.brand);
    }
    
    setFilteredProducts(filtered);
  };

  const handleRowExpand = (productId, isExpanded) => {
    console.log(`Product ${productId} ${isExpanded ? 'expanded' : 'collapsed'}`);
  };

  const handleReviewClick = (productId) => {
    window.location.href = `/manual-review-interface?product=${productId}`;
  };

  const handleExportClick = (productId) => {
    console.log(`Export product ${productId}`);
  };

  const handleBulkReview = () => {
    const ambiguousProducts = filteredProducts?.filter(p => p?.matchType === 'ambiguous');
    if (ambiguousProducts?.length > 0) {
      window.location.href = '/manual-review-interface';
    }
  };

  const handleGenerateReport = () => {
    window.location.href = '/export-and-reports-center';
  };

  const handleExportQueueDownload = (index) => {
    console.log(`Download export ${index}`);
  };

  const handleClearCompletedExports = () => {
    console.log('Clear completed exports');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Top Bar with Progress and Status */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <WorkflowProgressIndicator 
                  currentStep={3}
                  completedSteps={['upload', 'configure', 'process']}
                />
                
                <ProcessingStatusBadge 
                  status="completed"
                  message="Analysis complete"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <AmbiguousMatchesCounter 
                  count={summaryMetrics?.ambiguousMatches}
                  priority="medium"
                  onClick={handleBulkReview}
                />
                
                <ExportQueueIndicator 
                  queueCount={0}
                  completedCount={2}
                  onDownloadClick={handleExportQueueDownload}
                  onClearCompleted={handleClearCompletedExports}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Results Dashboard</h1>
                <p className="text-muted-foreground mt-2">
                  Analysis completed in {summaryMetrics?.processingTime} • {summaryMetrics?.matchesFound} of {summaryMetrics?.totalProducts} products matched
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  iconName={showAnalytics ? 'EyeOff' : 'Eye'}
                  iconPosition="left"
                >
                  {showAnalytics ? 'Hide' : 'Show'} Analytics
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleBulkReview}
                  iconName="Eye"
                  iconPosition="left"
                >
                  Review Ambiguous ({summaryMetrics?.ambiguousMatches})
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={handleGenerateReport}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Generate Report
                </Button>
                
                <ExportDropdown 
                  onExport={(format, options) => {
                    console.log(`Export format: ${format}`, options);
                  }}
                />
              </div>
            </div>
          </div>

          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricsCard
              title="Total Products"
              value={summaryMetrics?.totalProducts?.toLocaleString()}
              subtitle="Processed from all suppliers"
              icon="Package"
              color="primary"
            />
            
            <MetricsCard
              title="Matches Found"
              value={summaryMetrics?.matchesFound?.toLocaleString()}
              subtitle={`${((summaryMetrics?.matchesFound / summaryMetrics?.totalProducts) * 100)?.toFixed(1)}% match rate`}
              icon="Target"
              color="success"
              trend={12.5}
            />
            
            <MetricsCard
              title="Potential Savings"
              value={formatCurrency(summaryMetrics?.potentialSavings)}
              subtitle="Identified cost reductions"
              icon="DollarSign"
              color="success"
              trend={8.3}
            />
            
            <MetricsCard
              title="Needs Review"
              value={summaryMetrics?.ambiguousMatches}
              subtitle="Ambiguous matches"
              icon="AlertTriangle"
              color="warning"
              onClick={handleBulkReview}
            />
          </div>

          {/* Additional Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <MetricsCard
              title="Exact Matches"
              value={summaryMetrics?.exactMatches}
              subtitle="EAN/MPN matches"
              icon="CheckCircle"
              color="success"
            />
            
            <MetricsCard
              title="AI Matches"
              value={summaryMetrics?.aiMatches}
              subtitle="Fuzzy description matches"
              icon="Zap"
              color="warning"
            />
            
            <MetricsCard
              title="Processing Time"
              value={summaryMetrics?.processingTime}
              subtitle="Total analysis duration"
              icon="Clock"
              color="primary"
            />
          </div>

          {/* Analytics Panel */}
          {showAnalytics && (
            <div className="mb-8">
              <SupplierAnalytics />
            </div>
          )}

          {/* Filter Controls */}
          <FilterControls
            onFilterChange={handleFilterChange}
            onSearchChange={(search) => handleFilterChange({ ...filters, search })}
            onClearFilters={() => {
              setFilters({});
              setFilteredProducts(mockProducts);
            }}
            totalResults={mockProducts?.length}
            filteredResults={filteredProducts?.length}
          />

          {/* Products Table */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-foreground">Product Results</h2>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Info" size={14} />
                  <span>Click rows to expand supplier details</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => setFilteredProducts([...mockProducts])}
                >
                  Refresh
                </Button>
              </div>
            </div>
            
            <ProductsTable
              products={filteredProducts}
              onRowExpand={handleRowExpand}
              onReviewClick={handleReviewClick}
              onExportClick={handleExportClick}
            />
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                onClick={() => window.location.href = '/manual-review-interface'}
                iconName="Eye"
                iconPosition="left"
                fullWidth
              >
                Review All Ambiguous Matches
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.location.href = '/export-and-reports-center'}
                iconName="FileText"
                iconPosition="left"
                fullWidth
              >
                Generate Detailed Reports
              </Button>
              
              <Button
                variant="outline"
                onClick={() => window.location.href = '/processing-status-monitor'}
                iconName="Activity"
                iconPosition="left"
                fullWidth
              >
                Process New Files
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResultsDashboard;